import { useEffect, useMemo, useRef, useState } from "react";
import Lottie from "lottie-react";
import { GAME_POTIONS, getGameNavPotionIdsForPlayMode } from "../constants/potions";
import { useGameSessionNav } from "../contexts/GameSessionContext";
import { resolvePotionStarsAnimation } from "../utils/colorizeLottieAnimation";
import "./VocabTooltip.css";

const POTION_BUBBLE_DIAMETER = 58;
const POTION_BUBBLE_OVERLAP = 14;

function buildPotionBubbleLayout(count) {
  const diameter = POTION_BUBBLE_DIAMETER;
  const overlap = POTION_BUBBLE_OVERLAP;
  const radius = diameter / 2;
  const step = diameter - overlap;
  const safeCount = Math.max(count, 1);
  const circles = Array.from({ length: safeCount }, (_, index) => ({
    cx: radius + index * step,
    cy: radius,
    r: radius,
  }));

  return {
    circles,
    width: diameter + Math.max(0, safeCount - 1) * step,
    height: diameter,
    diameter,
    overlap,
    step,
  };
}

function PotionStar({ size = 12, className = "", gold = false }) {
  return (
    <span
      className={`game-potion-nav__star ${className}${
        gold ? " game-potion-nav__star--gold" : ""
      }`}
      aria-hidden="true"
    >
      <span className="game-potion-nav__star-core">
        <svg viewBox="0 0 24 24" width={size} height={size}>
          <path
            d="M12 2 L14.2 8.4 L21 8.4 L15.4 12.4 L17.6 18.8 L12 14.8 L6.4 18.8 L8.6 12.4 L3 8.4 L9.8 8.4 Z"
            fill="currentColor"
          />
        </svg>
      </span>
    </span>
  );
}

function PotionNavStars({ side }) {
  if (side === "right") {
    return (
      <span className="game-potion-nav__stars game-potion-nav__stars--right" aria-hidden="true">
        <PotionStar size={11} className="game-potion-nav__star--r1" gold />
        <PotionStar size={15} className="game-potion-nav__star--r2" />
        <PotionStar size={9} className="game-potion-nav__star--r3" gold />
        <PotionStar size={13} className="game-potion-nav__star--r4" />
      </span>
    );
  }

  return (
    <span className="game-potion-nav__stars game-potion-nav__stars--left" aria-hidden="true">
      <PotionStar size={13} className="game-potion-nav__star--l1" />
      <PotionStar size={10} className="game-potion-nav__star--l2" gold />
      <PotionStar size={16} className="game-potion-nav__star--l3" gold />
      <PotionStar size={8} className="game-potion-nav__star--l4" />
    </span>
  );
}

function GamePotionNavItem({
  potion,
  wrapperClass,
  opacity,
  canUse,
  alreadyUsed,
  title,
  tooltipId,
  onUse,
}) {
  const [hovered, setHovered] = useState(false);
  const [consumePhase, setConsumePhase] = useState("idle");
  const prevAlreadyUsedRef = useRef(alreadyUsed);
  const lottieRef = useRef(null);
  const starsAnimation = useMemo(
    () => resolvePotionStarsAnimation(potion),
    [potion]
  );

  useEffect(() => {
    if (!alreadyUsed) {
      setConsumePhase("idle");
      prevAlreadyUsedRef.current = false;
      return;
    }

    if (!prevAlreadyUsedRef.current) {
      setConsumePhase("animating");
    }

    prevAlreadyUsedRef.current = alreadyUsed;
  }, [alreadyUsed]);

  useEffect(() => {
    if (!hovered) {
      lottieRef.current?.stop();
      return;
    }
    lottieRef.current?.goToAndPlay(0, true);
  }, [hovered, starsAnimation]);

  const setActive = (active) => {
    if (!canUse || consumePhase !== "idle") return;
    setHovered(active);
  };

  const handleConsumeAnimationEnd = (event) => {
    if (event.animationName !== "game-potion-nav-consume") return;
    setConsumePhase("gone");
    setHovered(false);
  };

  const isConsuming = consumePhase === "animating";
  const isConsumed = consumePhase === "gone";
  const showPotion = !isConsumed;
  const wrapperStyle = isConsuming
    ? undefined
    : { opacity: isConsumed ? 0 : opacity };

  return (
    <div
      className={`game-potion-nav__entry vocab-tooltip-wrap${
        canUse && consumePhase === "idle" ? " game-potion-nav__entry--usable" : ""
      }${isConsumed ? " game-potion-nav__entry--consumed" : ""}`}
      style={{ "--potion-theme-color": potion.themeColor }}
      onMouseEnter={() => setActive(true)}
      onMouseLeave={() => setActive(false)}
    >
      <div
        className={`${wrapperClass}${
          isConsuming ? " game-potion-nav__item--consuming" : ""
        }${isConsumed ? " game-potion-nav__item--consumed" : ""}`}
        style={wrapperStyle}
        onAnimationEnd={handleConsumeAnimationEnd}
      >
        {showPotion ? (
          <button
            type="button"
            className="game-potion-nav__btn"
            disabled={!canUse || isConsuming}
            onClick={() => {
              if (canUse) onUse(potion.id);
            }}
            onFocus={() => setActive(true)}
            onBlur={() => setActive(false)}
            aria-label={title}
            aria-describedby={tooltipId}
            aria-hidden={isConsumed}
          >
            <span
              className={`game-potion-nav__glow${
                hovered ? " game-potion-nav__glow--active" : ""
              }`}
              aria-hidden="true"
            />
            <span
              className={`game-potion-nav__sparkle${
                hovered ? " game-potion-nav__sparkle--active" : ""
              }`}
              aria-hidden="true"
            >
              <Lottie
                lottieRef={lottieRef}
                animationData={starsAnimation}
                loop
                autoplay={false}
                className="game-potion-nav__sparkle-lottie"
                rendererSettings={{ preserveAspectRatio: "xMidYMid meet" }}
              />
            </span>
            <img
              src={potion.image}
              alt=""
              className={`game-potion-nav__image game-potion-nav__image--${potion.id}`}
              decoding="sync"
              draggable={false}
            />
          </button>
        ) : null}
      </div>
      {!isConsumed ? (
        <span id={tooltipId} className="vocab-tooltip vocab-tooltip--wide vocab-tooltip--below" role="tooltip">
          {potion.gameTooltipDescription ?? potion.description}
        </span>
      ) : null}
    </div>
  );
}

function getSwapPotionTitle(
  potion,
  { owned, alreadyUsed, canUse, unavailableReason, potionUsedThisQuestion, inputDisabled }
) {
  if (!owned) return `${potion.name} — non possédée`;
  if (alreadyUsed) return `${potion.name} — déjà utilisée cette partie`;
  if (canUse) return potion.name;
  if (unavailableReason === "never-played") {
    return `${potion.name} — ce mot n'a jamais été joué`;
  }
  if (unavailableReason === "not-three-plus") {
    return `${potion.name} — ce mot n'a pas encore été joué 3 fois`;
  }
  if (unavailableReason === "not-mastered") {
    return `${potion.name} — ce mot n'est pas maîtrisé (gradation ≥ B+ requise)`;
  }
  if (unavailableReason === "not-very-recent") {
    return `${potion.name} — ce mot n'a pas été joué récemment (0 à 7 jours)`;
  }
  if (unavailableReason === "last-question") {
    return `${potion.name} — dernière question, report impossible`;
  }
  if (unavailableReason === "deferred-question") {
    return `${potion.name} — question reportée (aucune potion utilisable)`;
  }
  if (unavailableReason === "no-candidates") {
    if (potion.id === "decouverte") {
      return `${potion.name} — aucun mot jamais joué disponible pour l'échange`;
    }
    if (potion.id === "bouillon-deja-vu") {
      return `${potion.name} — aucun mot joué exactement 1 fois disponible pour l'échange`;
    }
    if (potion.id === "mixture-3eme-oeil") {
      return `${potion.name} — aucun mot joué exactement 2 fois disponible pour l'échange`;
    }
    if (potion.id === "grog-veuve-noire") {
      return `${potion.name} — aucun mot gradé < B+ disponible pour l'échange`;
    }
    if (potion.id === "mixture-du-brucolaque") {
      return `${potion.name} — aucun mot moins récemment joué disponible pour l'échange`;
    }
    return `${potion.name} — aucun mot disponible pour l'échange`;
  }
  if (unavailableReason === "potion-used" || potionUsedThisQuestion) {
    return `${potion.name} — une potion a déjà été utilisée sur cette question`;
  }
  if (unavailableReason === "waiting" || inputDisabled) {
    return `${potion.name} — attendez la fin du décompte`;
  }
  return `${potion.name} — inutilisable sur cette question`;
}

function getPotionTitle(potion, state) {
  if (state.potionsLocked) {
    return `${potion.name} — question reportée (aucune potion utilisable)`;
  }
  if (potion.id === "decouverte") {
    return getSwapPotionTitle(potion, {
      ...state,
      canUse: state.discoveryCanUse,
      unavailableReason: state.discoveryUnavailableReason,
    });
  }
  if (potion.id === "bouillon-deja-vu") {
    return getSwapPotionTitle(potion, {
      ...state,
      canUse: state.bouillonCanUse,
      unavailableReason: state.bouillonUnavailableReason,
    });
  }
  if (potion.id === "mixture-3eme-oeil") {
    return getSwapPotionTitle(potion, {
      ...state,
      canUse: state.mixtureCanUse,
      unavailableReason: state.mixtureUnavailableReason,
    });
  }
  if (potion.id === "grog-veuve-noire") {
    return getSwapPotionTitle(potion, {
      ...state,
      canUse: state.grogCanUse,
      unavailableReason: state.grogUnavailableReason,
    });
  }
  if (potion.id === "mixture-du-brucolaque") {
    return getSwapPotionTitle(potion, {
      ...state,
      canUse: state.brucolaqueCanUse,
      unavailableReason: state.brucolaqueUnavailableReason,
    });
  }
  if (potion.id === "baume-sursis") {
    return getSwapPotionTitle(potion, {
      ...state,
      canUse: state.sursisCanUse,
      unavailableReason: state.sursisUnavailableReason,
    });
  }
  if (!state.owned) return `${potion.name} — non possédée`;
  if (state.alreadyUsed) return `${potion.name} — déjà utilisée cette partie`;
  if (state.potionUsedThisQuestion) {
    return `${potion.name} — une potion a déjà été utilisée sur cette question`;
  }
  if (state.inputDisabled) return `${potion.name} — question terminée`;
  return potion.name;
}

export default function GamePotionNav({ visible = true }) {
  const { navState, handleUsePotion } = useGameSessionNav();

  const navPotionIds = getGameNavPotionIdsForPlayMode(navState.playMode);
  const navPotions = useMemo(() => {
    const potionsById = new Map(GAME_POTIONS.map((potion) => [potion.id, potion]));
    return navPotionIds.map((id) => potionsById.get(id)).filter(Boolean);
  }, [navPotionIds]);
  const bubbleLayout = useMemo(
    () => buildPotionBubbleLayout(navPotions.length),
    [navPotions.length]
  );

  if (!visible || !navState.visible) return null;

  const {
    potionCounts = {},
    usedPotionIds = [],
    potionUsedThisQuestion = false,
    inputDisabled = false,
    discoveryCanUse = false,
    discoveryUnavailableReason = "",
    bouillonCanUse = false,
    bouillonUnavailableReason = "",
    mixtureCanUse = false,
    mixtureUnavailableReason = "",
    grogCanUse = false,
    grogUnavailableReason = "",
    brucolaqueCanUse = false,
    brucolaqueUnavailableReason = "",
    sursisCanUse = false,
    sursisUnavailableReason = "",
    potionsLocked = false,
  } = navState;

  const swapPotionStateById = {
    decouverte: {
      canUse: discoveryCanUse,
      unavailableReason: discoveryUnavailableReason,
    },
    "bouillon-deja-vu": {
      canUse: bouillonCanUse,
      unavailableReason: bouillonUnavailableReason,
    },
    "mixture-3eme-oeil": {
      canUse: mixtureCanUse,
      unavailableReason: mixtureUnavailableReason,
    },
    "grog-veuve-noire": {
      canUse: grogCanUse,
      unavailableReason: grogUnavailableReason,
    },
    "mixture-du-brucolaque": {
      canUse: brucolaqueCanUse,
      unavailableReason: brucolaqueUnavailableReason,
    },
    "baume-sursis": {
      canUse: sursisCanUse,
      unavailableReason: sursisUnavailableReason,
    },
  };

  return (
    <div className="menuFilterContainer game-potion-nav">
      <div className="game-potion-nav__frame">
        <PotionNavStars side="left" />
        <div
          className="game-potion-nav__bubble-track"
          style={{
            "--potion-bubble-diameter": `${bubbleLayout.diameter}px`,
            "--potion-bubble-overlap": `${bubbleLayout.overlap}px`,
            "--potion-bubble-step": `${bubbleLayout.step}px`,
            width: `${bubbleLayout.width}px`,
          }}
        >
          <svg
            className="game-potion-nav__bubbles"
            viewBox={`0 0 ${bubbleLayout.width} ${bubbleLayout.height}`}
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          >
            {bubbleLayout.circles.map((circle, index) => (
              <circle
                key={`bubble-${index}`}
                cx={circle.cx}
                cy={circle.cy}
                r={circle.r}
              />
            ))}
          </svg>
          <div className="game-potion-nav__items">
            {navPotions.map((potion) => {
        const owned = (potionCounts[potion.id] || 0) > 0;
        const swapState = swapPotionStateById[potion.id];
        const isSwapPotion = Boolean(swapState);
        const alreadyUsed = usedPotionIds.includes(potion.id);
        const tooltipId = `game-potion-tooltip-${potion.id}`;

        let unavailableReason = "";
        if (potionsLocked) {
          unavailableReason = "deferred-question";
        } else if (isSwapPotion) {
          unavailableReason = swapState.unavailableReason;
        } else if (potionUsedThisQuestion) {
          unavailableReason = "potion-used";
        } else if (inputDisabled) {
          unavailableReason = "waiting";
        }

        const swapCanUse = swapState?.canUse ?? false;
        const canUse = potionsLocked
          ? false
          : isSwapPotion
          ? owned &&
            !alreadyUsed &&
            !potionUsedThisQuestion &&
            !inputDisabled &&
            swapCanUse
          : owned && !alreadyUsed && !potionUsedThisQuestion && !inputDisabled;

        let wrapperClass = "filterIcon game-potion-nav__item";
        if (canUse) {
          wrapperClass += " filterIcon--enabled";
        } else if (owned && !alreadyUsed) {
          wrapperClass += " filterIcon--disabled game-potion-nav__item--hint";
        }

        let opacity = 0.35;
        if (owned && !alreadyUsed) {
          opacity = canUse ? 1 : 0.55;
        } else if (owned && alreadyUsed) {
          opacity = 1;
        }

        const title = getPotionTitle(potion, {
          owned,
          alreadyUsed,
          discoveryCanUse,
          bouillonCanUse,
          mixtureCanUse,
          grogCanUse,
          brucolaqueCanUse,
          discoveryUnavailableReason,
          bouillonUnavailableReason,
          mixtureUnavailableReason,
          grogUnavailableReason,
          brucolaqueUnavailableReason,
          sursisCanUse,
          sursisUnavailableReason,
          potionsLocked,
          unavailableReason,
          potionUsedThisQuestion,
          inputDisabled,
        });

        return (
          <GamePotionNavItem
            key={potion.id}
            potion={potion}
            wrapperClass={wrapperClass}
            opacity={opacity}
            canUse={canUse}
            alreadyUsed={alreadyUsed}
            title={title}
            tooltipId={tooltipId}
            onUse={handleUsePotion}
          />
        );
            })}
          </div>
        </div>
        <PotionNavStars side="right" />
      </div>
    </div>
  );
}
